/**
 * Task 204 — the contextual "save reading point" menu that appears near a text
 * selection in the Corpus Reader.
 *
 * Positioning: a `position: fixed` chip anchored to the selection's top edge,
 * horizontally centered on the selection and clamped to the viewport so it never
 * spills off-screen. It flips below the selection when there isn't room above
 * (near the top of the viewport). Being fixed + out of flow, it never reflows the
 * reading text (a hard requirement). `onMouseDown` is prevented so clicking the
 * chip does NOT collapse the selection before the click handler runs — the
 * standard selection-toolbar trick — which also keeps native copy working.
 */
import type { JSX } from "react";
import { BowSigil } from "../components/Wordmark";
import type { Strings } from "../strings/types";
import type { SelectionGeometry } from "./use-text-selection";

const EDGE_MARGIN = 12; // keep the chip this far from the viewport edges
const GAP = 10; // distance between the chip and the selection edge
const FLIP_THRESHOLD = 64; // place below when the selection top is nearer than this

export function SelectionBookmarkMenu(props: {
  selection: SelectionGeometry;
  strings: Strings;
  /** Receives the geometry SNAPSHOT this menu rendered with — never the live
   *  selection, which may already have collapsed by the time the tap lands. */
  onSave: (selection: SelectionGeometry) => void;
}): JSX.Element {
  const { selection, strings, onSave } = props;
  const below = selection.bounds.top < FLIP_THRESHOLD;
  const viewportW = typeof window !== "undefined" ? window.innerWidth : 1024;
  const centerX = selection.bounds.left + selection.bounds.width / 2;
  const clampedX = Math.min(Math.max(centerX, EDGE_MARGIN), viewportW - EDGE_MARGIN);
  const y = below ? selection.bounds.bottom + GAP : selection.bounds.top - GAP;

  return (
    <div
      className="creader-selmenu"
      data-below={below ? "true" : "false"}
      role="dialog"
      aria-label={strings.corpusBookmarkMenuLabel}
      style={{ left: `${clampedX}px`, top: `${y}px` }}
    >
      <button
        type="button"
        className="creader-selmenu__btn"
        // Preserve the selection: don't let the mousedown collapse it.
        onMouseDown={(e) => e.preventDefault()}
        // Touch: without this, the tap collapses the selection on touchstart,
        // the hook nulls it, and the menu unmounts before `click` ever fires —
        // save would silently fail on mobile. preventDefault keeps the selection
        // alive but also suppresses the synthetic click, so touchend saves.
        onTouchStart={(e) => e.preventDefault()}
        onTouchEnd={(e) => {
          e.preventDefault();
          onSave(selection);
        }}
        onClick={() => onSave(selection)}
      >
        <BowSigil className="creader-selmenu__sigil" />
        {strings.corpusBookmarkSave}
      </button>
    </div>
  );
}
